import { useEffect, useState } from "react";
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle
} from "@/components/ui/resizable";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import CommentSection from "@/components/article/CommentSection";
import { BookOpen } from "lucide-react";
import { questions, MLQuestion } from "../../ml-fundamentals/questions";

// Declare KaTeX auto-render on window object
declare global {
  interface Window {
    renderMathInElement?: (el: HTMLElement, options?: any) => void;
  }
}

const MLTheoryCheatsheet = () => {
  const [selectedQuestion, setSelectedQuestion] = useState<MLQuestion>(questions[0]);
  const [content, setContent] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [katexLoaded, setKatexLoaded] = useState(false);

  // Load KaTeX and auto-render script
  useEffect(() => {
    if (window.renderMathInElement) {
      setKatexLoaded(true);
      return;
    }

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://cdn.jsdelivr.net/npm/katex@0.16.8/dist/katex.min.css';
    document.head.appendChild(link);

    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/katex@0.16.8/dist/katex.min.js';
    script.async = true;
    document.head.appendChild(script);

    const autoScript = document.createElement('script');
    autoScript.src = 'https://cdn.jsdelivr.net/npm/katex@0.16.8/dist/contrib/auto-render.min.js';
    autoScript.async = true;
    autoScript.onload = () => setKatexLoaded(true);
    document.head.appendChild(autoScript);
  }, []);

  const processContentWithKatex = (element: Element) => {
    if (window.renderMathInElement) {
      try {
        window.renderMathInElement(element as HTMLElement, {
          delimiters: [
            { left: "\\(", right: "\\)", display: false },
            { left: "\\[", right: "\\]", display: true },
            { left: "$$", right: "$$", display: true },
            { left: "$", right: "$", display: false }
          ],
          throwOnError: false
        });
      } catch (err) {
        console.error('KaTeX render failed:', err);
      }
    } else {
      console.log('KaTeX not available for processing');
    }
  };

  const loadQuestionContent = async (questionId: number) => {
    try {
      setIsLoading(true);
      setError(null);
      
      console.log(`Attempting to load question ${questionId} from /ml-theory/qn${questionId}/index.html`);
      
      // Try loading from the individual question directory
      const response = await fetch(`/ml-theory/qn${questionId}/index.html`);
      
      if (response.ok) {
        const htmlContent = await response.text();
        console.log(`Successfully loaded qn${questionId} content:`, htmlContent.substring(0, 200) + "...");
        
        // Extract just the body content and fix LaTeX delimiters
        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlContent, 'text/html');
        let bodyContent = doc.body.innerHTML;
        
        // More precise LaTeX delimiter fixing
        // Fix display math: convert standalone [math] to \[math\]
        bodyContent = bodyContent.replace(/\[\s*\n?\s*([^[\]]*(?:\n[^[\]]*)*)\s*\n?\s*\]/g, '\\[$1\\]');
        
        // Fix inline math: convert (math) to \(math\) - only when it looks like math
        bodyContent = bodyContent.replace(/\(\s*([^()]*(?:\\[^)]*|[^()\\])*)\s*\)/g, (match, inner) => {
          // Only convert if it contains math-like symbols
          if (/[\\=+\-*/^_{}\s]*[a-zA-Z\\^_{}\s]*[\\=+\-*/^_{}]/.test(inner) || inner.includes('\\')) {
            return `\\(${inner}\\)`;
          }
          return match; // Return original if it doesn't look like math
        });
        
        console.log('Content after LaTeX delimiter processing:', bodyContent.substring(0, 300));
        setContent(bodyContent);
        return;
      }
      
      console.log(`Failed to load qn${questionId}/index.html, status: ${response.status}`);
      throw new Error(`Failed to load question ${questionId}. File not found at /ml-theory/qn${questionId}/index.html`);
      
    } catch (err) {
      console.error('Error loading ML question:', err);
      setError(err instanceof Error ? err.message : 'Failed to load question');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (katexLoaded) {
      loadQuestionContent(selectedQuestion.id);
    }
  }, [selectedQuestion.id, katexLoaded]);

  // Process content when it changes and KaTeX is available
  useEffect(() => {
    if (katexLoaded && content) {
      // Wait for the DOM to be updated with new content
      setTimeout(async () => {
        const contentElement = document.querySelector('.math-content');
        if (contentElement) {
          console.log('Found content element, processing KaTeX');
          await processContentWithKatex(contentElement);
        } else {
          console.log('Content element not found');
        }
      }, 300);
    }
  }, [content, katexLoaded]);

  const handleQuestionSelect = (questionId: string) => {
    const question = questions.find(q => q.id === parseInt(questionId));
    if (question) {
      setSelectedQuestion(question);
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case "Easy":
        return "bg-green-600";
      case "Medium":
        return "bg-yellow-600";
      case "Hard":
        return "bg-red-600";
      default:
        return "bg-gray-600";
    }
  };

  if (isLoading || !katexLoaded) {
    return (
      <div className="w-full mb-6">
        <Card className="border bg-slate-900 text-slate-100 shadow-md overflow-hidden">
          <CardHeader className="bg-slate-800 border-b border-slate-700">
            <div className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-purple-400" />
              <CardTitle className="text-slate-100 text-xl">ML Theory Cheatsheet</CardTitle>
            </div>
            <CardDescription className="text-slate-400">
              {!katexLoaded ? 'Loading math renderer...' : 'Loading ML content...'}
            </CardDescription>
          </CardHeader>
          <div className="h-[600px] flex items-center justify-center">
            <div className="text-slate-400">
              {!katexLoaded ? 'Initializing KaTeX...' : `Loading question ${selectedQuestion.id}...`}
            </div>
          </div>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full mb-6">
        <Card className="border bg-slate-900 text-slate-100 shadow-md overflow-hidden">
          <CardHeader className="bg-slate-800 border-b border-slate-700">
            <div className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-purple-400" />
              <CardTitle className="text-slate-100 text-xl">ML Theory Cheatsheet</CardTitle>
            </div>
            <CardDescription className="text-slate-400">
              Error loading content
            </CardDescription>
          </CardHeader>
          <div className="h-[600px] flex items-center justify-center">
            <div className="text-red-400 p-4 text-center">
              <p className="mb-2">Error: {error}</p>
              <p className="text-sm text-slate-400">
                Make sure to run: <code className="bg-slate-700 px-2 py-1 rounded">cd astro && npm run build</code>
              </p>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="w-full mb-6">
      <Card className="border bg-slate-900 text-slate-100 shadow-md overflow-hidden">
        <CardHeader className="bg-slate-800 border-b border-slate-700">
          <div className="flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-purple-400" />
            <CardTitle className="text-slate-100 text-xl">ML Theory Cheatsheet</CardTitle>
          </div>
          <CardDescription className="text-slate-400">
            Quick reference for common ML interview questions
          </CardDescription>
        </CardHeader>

        <ResizablePanelGroup
          direction="horizontal"
          className="h-[600px]"
        >
          <ResizablePanel
            defaultSize={30}
            minSize={25}
            className="bg-slate-800 border-r border-slate-700"
          >
            <ScrollArea className="h-full">
              <div className="p-4">
                <div className="mb-4">
                  <Select value={selectedQuestion.id.toString()} onValueChange={handleQuestionSelect}>
                    <SelectTrigger className="w-full bg-slate-700 border-slate-600 text-slate-200">
                      <SelectValue placeholder="Select a question" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-700 border-slate-600">
                      {questions.map((question) => (
                        <SelectItem 
                          key={question.id} 
                          value={question.id.toString()}
                          className="text-slate-200 focus:bg-slate-600 focus:text-slate-100"
                        >
                          {question.short}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="p-4 bg-slate-700 rounded">
                  <p className="font-medium text-slate-200 mb-2">{selectedQuestion.short}</p>
                  <p className="text-sm text-slate-300 mb-3">{selectedQuestion.question}</p>
                  <div className="flex flex-wrap gap-2 text-xs">
                    <Badge variant="secondary" className="bg-slate-600 text-slate-200">
                      {selectedQuestion.company}
                    </Badge>
                    <Badge className={`${getDifficultyColor(selectedQuestion.difficulty)} text-white`}>
                      {selectedQuestion.difficulty}
                    </Badge>
                  </div>
                </div>
              </div>
              <ScrollBar orientation="vertical" className="bg-slate-600" />
            </ScrollArea>
          </ResizablePanel>
          <ResizableHandle withHandle />
          <ResizablePanel defaultSize={70} className="bg-slate-900">
            <ScrollArea className="h-full">
              <div className="p-6">
                <div 
                  className="math-content prose prose-sm max-w-none prose-invert text-slate-300"
                  dangerouslySetInnerHTML={{ __html: content }}
                />
                <div className="mt-6">
                  <CommentSection articleId={`ml-question-${selectedQuestion.id}`} />
                </div>
              </div>
              <ScrollBar orientation="vertical" className="bg-slate-600" />
            </ScrollArea>
          </ResizablePanel>
        </ResizablePanelGroup>
      </Card>
    </div>
  );
};

export default MLTheoryCheatsheet;
